<template>
  <b-card title="Heatmap Polygons - Demand Zones">
    <b-form @submit.prevent="fetchHeatmapData">
      <b-row>
        <!-- Zone Selection -->
        <b-col md="8">
          <b-form-group label="Select Zone" label-for="zone-select">
            <v-select
              id="zone-select"
              v-model="selectedZone"
              label="name"
              :options="zones"
              :loading="loadingZones"
              placeholder="Select a zone to view demand heatmap"
              @input="onZoneSelected"
            >
              <template #no-options>
                No zones available
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <!-- Fetch Button -->
        <b-col md="4" class="d-flex align-items-end pb-1">
          <b-button
            :disabled="!selectedZoneId || fetching"
            variant="primary"
            block
            type="submit"
          >
            <b-spinner v-if="fetching" small />
            <span v-else>Load Heatmap</span>
          </b-button>
        </b-col>

        <!-- Map Container -->
        <b-col md="12" class="mt-2">
          <div v-if="selectedZoneId" id="heatmap-map" class="map-container" />
          <b-alert v-else variant="info" show>
            <div class="alert-body">
              <feather-icon icon="InfoIcon" class="mr-50" />
              <span>Please select a zone to view the demand heatmap</span>
            </div>
          </b-alert>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import {
  ref,
  computed,
  nextTick,
  watch,
} from "@vue/composition-api";
import {
  BCard,
  BFormGroup,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BForm,
  BAlert,
} from "bootstrap-vue";
import vSelect from "vue-select";
import axiosIns from "@/libs/axios";
import {
  errorNotification,
  successNotification,
} from "@/utils/toast-notifications";
import useZoneList from "@/views/zones/useZoneList";
import useRegisterStoreModule from "@/hooks/useRegisterStoreModule";
import zonesStore from "@/views/zones/zoneStoreModule";

export default {
  components: {
    BCard,
    BFormGroup,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BForm,
    BAlert,
    vSelect,
  },
  setup() {

    useRegisterStoreModule("zones", zonesStore);

    const { tableData: fetchedZones, loadingZones } = useZoneList();
    const zones = computed(
      () => fetchedZones.value?.filter((el) => el.area === "None") || []
    );
    const selectedZone = ref(null);
    const selectedZoneId = ref(null);

    const map = ref(null);
    const hexagons = ref([]);
    const googlePolygons = ref([]);
    const fetching = ref(false);

    const initMap = () => {
      if (!window.google) {
        console.error("Google Maps is not loaded");
        errorNotification("Google Maps is not loaded");
        return false;
      }

      const mapElement = document.getElementById("heatmap-map");
      if (!mapElement) {
        console.error("Map container element not found");
        errorNotification("Map container not found");
        return false;
      }

      try {
        map.value = new google.maps.Map(mapElement, {
          zoom: 12,
          center: { lat: 31.963158, lng: 35.930359 },
          mapTypeId: "roadmap",
        });
        console.log("Map initialized successfully");
        return true;
      } catch (error) {
        console.error("Error initializing map:", error);
        errorNotification("Failed to initialize map");
        return false;
      }
    };

    const clearPolygons = () => {
      googlePolygons.value.forEach((polygon) => {
        polygon.setMap(null);
      });
      googlePolygons.value = [];
    };

    const getPolygonColor = (score) => {
      if (score > 500) {
        return "#93000A";
      } else if (score > 400) {
        return "#BA1A1A";
      } else if (score > 300) {
        return "#DE3730";
      } else if (score > 200) {
        return "#FF5449";
      } else if (score > 100) {
        return "#FFB4AB";
      } else {
        return "#2196F3";
      }
    };

    const renderPolygons = async () => {
      console.log("Rendering polygons, map exists:", !!map.value);

      if (!map.value) {
        console.log("Map not initialized, initializing now...");
        await nextTick();
        const initialized = initMap();

        if (!initialized) {
          errorNotification("Failed to initialize map");
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      if (!map.value) {
        console.error("Map still not initialized");
        errorNotification("Failed to initialize map");
        return;
      }

      console.log("Map is ready, rendering", hexagons.value.length, "polygons");
      clearPolygons();

      const bounds = new google.maps.LatLngBounds();

      googlePolygons.value = hexagons.value.map((hexagon) => {
        const coordinates = hexagon.boundary.coordinates[0].map((coord) => {
          const latLng = { lat: coord[1], lng: coord[0] };
          bounds.extend(latLng);
          return latLng;
        });

        const fillColor = getPolygonColor(hexagon.score);
        
        const polygon = new google.maps.Polygon({
          paths: coordinates,
          strokeColor: fillColor,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: fillColor,
          fillOpacity: 0.35,
        });

        const contentString = `
          <div style="padding: 10px; min-width: 250px;">
            <h5 style="margin-bottom: 10px; font-weight: bold;">Hexagon: ${
              hexagon.hexagonId
            }</h5>
            <div style="display: flex; flex-direction: column; gap: 5px;">
              <div><strong>Score:</strong> ${hexagon.score.toFixed(2)}</div>
              <div><strong>Available Drivers:</strong> ${
                hexagon.availableDrivers
              }</div>
              <div><strong>Unavailable Drivers:</strong> ${
                hexagon.unavailableDrivers
              }</div>
              <div><strong>Active Requests:</strong> ${
                hexagon.activeRequests
              }</div>
              <div><strong>Accepted Requests:</strong> ${
                hexagon.acceptedRequests
              }</div>
              <div><strong>Timestamp:</strong> ${new Date(
                hexagon.timestamp
              ).toLocaleString()}</div>
            </div>
          </div>
        `;

        const infoWindow = new google.maps.InfoWindow({
          content: contentString,
        });

        polygon.addListener("click", () => {
          googlePolygons.value.forEach((p) => {
            if (p.infoWindow) {
              p.infoWindow.close();
            }
          });
          infoWindow.setPosition(polygon.getPath().getAt(0));
          infoWindow.open(map.value);
        });

        polygon.infoWindow = infoWindow;
        polygon.setMap(map.value);

        return polygon;
      });

      if (hexagons.value.length > 0) {
        map.value.fitBounds(bounds);
      }
    };

    const onZoneSelected = async (event) => {
      if (!event) {
        selectedZoneId.value = null;
        hexagons.value = [];
        clearPolygons();
        map.value = null;
        return;
      }
      selectedZoneId.value = `${event.country}::${event.city}`;

      await nextTick();

      setTimeout(() => {
        if (!map.value) {
          console.log("Attempting to initialize map...");
          initMap();
        }
      }, 100);
    };

    const fetchHeatmapData = async () => {
      if (!selectedZoneId.value) {
        errorNotification("Please select a zone first");
        return;
      }

      fetching.value = true;
      hexagons.value = [];
      clearPolygons();

      try {
        const response = await axiosIns.get(
          `/zones/${selectedZoneId.value}/hexagons/demand`
        );
        hexagons.value = response.data.data.hexagons || [];

        if (hexagons.value.length === 0) {
          errorNotification("No hexagon data available for this zone");
        } else {
          successNotification(`Loaded ${hexagons.value.length} hexagons`);
          await nextTick();
          await renderPolygons();
        }
      } catch (error) {
        console.error("Error fetching heatmap data:", error);
        errorNotification(
          error.response?.data?.message || "Failed to fetch heatmap data"
        );
      } finally {
        fetching.value = false;
      }
    };

    watch(selectedZoneId, async (newValue) => {
      if (newValue && !map.value) {
        await nextTick();
        setTimeout(() => {
          if (!map.value) {
            console.log("Watch: Attempting to initialize map...");
            initMap();
          }
        }, 150);
      }
    });

    return {
      zones,
      loadingZones,
      selectedZone,
      selectedZoneId,
      onZoneSelected,
      hexagons,
      fetching,
      fetchHeatmapData,
    };
  },
};
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 600px;
  border-radius: 0.357rem;
  overflow: hidden;
  background-color: #e5e5e5;
}

#heatmap-map {
  width: 100%;
  height: 100%;
  min-height: 600px;
}
</style>
